import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthHeader } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuthHeader(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, referral_code) => {
    const payload = { email, password, name };
    if (referral_code) payload.referral_code = referral_code;
    const { data } = await api.post("/auth/register", payload);
    setAuthHeader(data.token);
    setUser(data.user);
    return data.user;
  };

  const exchangeSession = async (session_id) => {
    const { data } = await api.post("/auth/session", { session_id });
    setAuthHeader(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuthHeader(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, exchangeSession, refresh: checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
